export const sendTelemetry = async (context: string) => {
    try {
        // Wait a moment for layout to settle
        await new Promise(resolve => setTimeout(resolve, 1000));

        // Create a temporary div to measure safe area insets
        const safeAreaDiv = document.createElement('div');
        safeAreaDiv.style.position = 'fixed';
        safeAreaDiv.style.top = '0';
        safeAreaDiv.style.bottom = '0';
        safeAreaDiv.style.left = '0';
        safeAreaDiv.style.right = '0';
        safeAreaDiv.style.paddingTop = 'env(safe-area-inset-top)';
        safeAreaDiv.style.paddingBottom = 'env(safe-area-inset-bottom)';
        safeAreaDiv.style.visibility = 'hidden';
        safeAreaDiv.style.pointerEvents = 'none';
        safeAreaDiv.style.zIndex = '-9999';
        document.body.appendChild(safeAreaDiv);

        const computedStyle = window.getComputedStyle(safeAreaDiv);
        const safeAreaTop = computedStyle.paddingTop;
        const safeAreaBottom = computedStyle.paddingBottom;

        document.body.removeChild(safeAreaDiv);

        // Get element details including computed styles
        const getElementDetails = (selector: string) => {
            const el = document.querySelector(selector);
            if (!el) return null;
            const rect = el.getBoundingClientRect();
            const style = window.getComputedStyle(el);

            return {
                rect: {
                    top: Math.round(rect.top),
                    bottom: Math.round(rect.bottom),
                    height: Math.round(rect.height),
                    width: Math.round(rect.width)
                },
                style: {
                    bg: style.backgroundColor,
                    bgImg: style.backgroundImage !== 'none' ? 'present' : 'none',
                    pad: `${style.paddingTop} ${style.paddingRight} ${style.paddingBottom} ${style.paddingLeft}`,
                    marg: `${style.marginTop} ${style.marginRight} ${style.marginBottom} ${style.marginLeft}`,
                    overflow: style.overflow,
                    position: style.position,
                    height: style.height,
                    minHeight: style.minHeight,
                    // Add positioning values to debug fixed positioning
                    top: style.top,
                    bottom: style.bottom,
                    left: style.left,
                    right: style.right
                },
                // Gap analysis: how far is the element's bottom from the viewport bottom?
                gapToViewportBottom: window.innerHeight - Math.round(rect.bottom)
            };
        };

        // Check what element is at specific screen positions (top and bottom)
        const getElementAtPoint = (x: number, y: number) => {
            const el = document.elementFromPoint(x, y);
            if (!el) return null;
            const style = window.getComputedStyle(el);
            return {
                tag: el.tagName,
                class: el.className,
                id: el.id,
                bg: style.backgroundColor,
                bgImg: style.backgroundImage !== 'none' ? 'has-bg-img' : 'none'
            };
        };

        const centerX = window.innerWidth / 2;

        // Calculate total screen height including safe areas
        const safeAreaBottomPx = parseInt(safeAreaBottom, 10) || 0;
        const safeAreaTopPx = parseInt(safeAreaTop, 10) || 0;
        const totalHeightWithSafeAreas = window.innerHeight + safeAreaBottomPx + safeAreaTopPx;

        // Check if visualViewport API is available (more accurate on mobile)
        const visualViewport = window.visualViewport;

        const data = {
            ctx: context,
            ts: new Date().toISOString(),
            ua: navigator.userAgent,
            view: {
                w: window.innerWidth,
                h: window.innerHeight,
                outerH: window.outerHeight,
                dpr: window.devicePixelRatio,
                scroll: window.scrollY,
                // Add screen dimensions for comparison
                screenH: window.screen.height,
                screenAvailH: window.screen.availHeight,
                // Visual viewport (if available)
                vvH: visualViewport?.height,
                vvOffsetTop: visualViewport?.offsetTop,
                // Document dimensions
                docClientH: document.documentElement.clientHeight,
                docScrollH: document.documentElement.scrollHeight,
                bodyClientH: document.body.clientHeight,
                // Calculated total with safe areas
                totalWithSafe: totalHeightWithSafeAreas
            },
            safe: {
                t: safeAreaTop,
                b: safeAreaBottom,
                tPx: safeAreaTopPx,
                bPx: safeAreaBottomPx
            },
            // Check what's at key positions
            atPoint: {
                top5: getElementAtPoint(centerX, 5),
                top30: getElementAtPoint(centerX, 30),
                bottom5: getElementAtPoint(centerX, window.innerHeight - 5),
                bottom30: getElementAtPoint(centerX, window.innerHeight - 30),
                bottom50: getElementAtPoint(centerX, window.innerHeight - 50)
            },
            // Flatten element details for better logging
            html: getElementDetails('html'),
            body: getElementDetails('body'),
            root: getElementDetails('#root'),
            alien: getElementDetails('.alien2048-container'),
            mfers: getElementDetails('.gameWiderContainer'),
            // Debug: What styles are actually applied to html?
            htmlDebug: (() => {
                const html = document.documentElement;
                const style = window.getComputedStyle(html);
                return {
                    // Check what's setting the height
                    offsetHeight: html.offsetHeight,
                    scrollHeight: html.scrollHeight,
                    clientHeight: html.clientHeight,
                    // CSS values
                    cssHeight: style.height,
                    cssMinHeight: style.minHeight,
                    cssMaxHeight: style.maxHeight,
                    // Parent info (html's parent is document, but let's check)
                    parentNode: html.parentNode?.nodeName || 'none',
                    // Check if any inline styles
                    inlineStyle: html.style.cssText || 'none',
                    // Check body.gameplay-page class
                    bodyHasGameplayClass: document.body.classList.contains('gameplay-page'),
                    // What stylesheets might be affecting html?
                    allCSSRules: 'check-devtools'
                };
            })()
        };

        // Send data using beacon if available (more reliable on unload) or fetch
        if (navigator.sendBeacon) {
            const blob = new Blob([JSON.stringify(data)], { type: 'application/json' });
            navigator.sendBeacon('/api/telemetry/log', blob);
        } else {
            await fetch('/api/telemetry/log', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(data),
            });
        }

        console.log('Telemetry sent successfully');
    } catch (error) {
        // Fail open - just log to console
        console.warn('Failed to send telemetry:', error);
    }
};
